/**
 * מאזן — the 5-step wizard (זכויות ותאריכים · צד א · צד ב · פרטי התיק · סיכום).
 *
 * Autosaves the draft to the server (debounced) so a customer can stop and come
 * back. Accessibility: the step heading takes focus on every step change, a
 * polite live region announces "שלב X מתוך 5", and a validation error names the
 * exact field (aria-invalid + aria-describedby) and moves focus to it.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react'
import { handleApiError } from '../../services/api'
import PersonStep from '../../features/mazan/components/PersonStep'
import SummaryStep from '../../features/mazan/components/SummaryStep'
import { ChoiceButtons, TextField } from '../../features/mazan/components/fields'
import {
  buildIntake, emptyPerson, mazanApi, personRowIssues, removeRows, SECTION_LABEL, STEP_LABELS, validateStep,
  type IntakeState, type MazanInputs, type Person, type RowIssue, type StepError,
} from '../../features/mazan/model'

const ERROR_ID = 'mz-step-error'

export default function MazanWizardPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [inputs, setInputs] = useState<MazanInputs | null>(null)
  const [step, setStep] = useState(0)
  const [intakes, setIntakes] = useState<(IntakeState | undefined)[]>([undefined, undefined])
  const [error, setError] = useState<StepError | null>(null)
  // after a failed "הבא" on a person step: list (live) every incomplete row
  const [showIssues, setShowIssues] = useState(false)
  const errorRef = useRef<HTMLDivElement>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const headingRef = useRef<HTMLHeadingElement>(null)
  const dirty = useRef(false)
  const latest = useRef<{ inputs: MazanInputs | null; step: number }>({ inputs: null, step: 0 })
  const timer = useRef<number>()
  const firstRender = useRef(true)

  // ── load ──
  useEffect(() => {
    mazanApi
      .get(id)
      .then((r) => {
        if (r.status !== 'draft') {
          navigate(`/Mazan/result/${id}`, { replace: true })
          return
        }
        const people = r.inputs.people.map((p) => ({ ...emptyPerson(), ...p })) as [Person, Person]
        setInputs({ ...r.inputs, people })
        setStep(Math.min(r.step || 0, STEP_LABELS.length - 1))
      })
      .catch((e) => setLoadError(handleApiError(e)))
  }, [id, navigate])

  // ── autosave ──
  const flush = useCallback(async () => {
    window.clearTimeout(timer.current)
    if (!dirty.current || !latest.current.inputs) return
    dirty.current = false
    setSaveState('saving')
    try {
      await mazanApi.save(id, latest.current.inputs, latest.current.step)
      setSaveState('saved')
    } catch (e) {
      dirty.current = true
      setSaveState('error')
      throw e
    }
  }, [id])

  useEffect(() => {
    latest.current = { inputs, step }
    if (!inputs) return
    dirty.current = true
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => { flush().catch(() => undefined) }, 900)
  }, [inputs, step, flush])

  useEffect(() => () => window.clearTimeout(timer.current), [])

  // ── focus management on step change (not on first load) ──
  const loaded = inputs !== null
  useEffect(() => {
    if (!loaded) return
    if (firstRender.current) { firstRender.current = false; return }
    headingRef.current?.focus()
  }, [step, loaded])

  useEffect(() => {
    if (!error) return
    // a list of problems → focus the panel so it is read out; a single field → focus that field
    if (error.issues || error.unadjusted) errorRef.current?.focus()
    else if (error.field) document.getElementById(error.field)?.focus()
  }, [error])

  if (loadError) return <p role="alert" className="m-6 rounded-lg bg-red-50 p-4 text-red-800">{loadError}</p>
  if (!inputs) return <p role="status" className="m-6 text-gray-700">טוען…</p>

  const update = (patch: Partial<MazanInputs>) => {
    setError(null)
    const next = { ...inputs, ...patch }
    // A new break date re-runs the clearing-house adjustment for both sides.
    if (patch.breakDate !== undefined && patch.breakDate !== inputs.breakDate) {
      const nextIntakes = [...intakes]
      next.people = next.people.map((p, i) => {
        const it = intakes[i]
        if (!it) return p
        const built = buildIntake(it.merged, patch.breakDate!)
        nextIntakes[i] = built.intake
        return { ...p, ...built.person }
      }) as [Person, Person]
      setIntakes(nextIntakes)
    }
    setInputs(next)
  }
  const setPerson = (i: 0 | 1) => (p: Person) => {
    setError(null)
    setInputs((cur) => (cur ? { ...cur, people: (i === 0 ? [p, cur.people[1]] : [cur.people[0], p]) as [Person, Person] } : cur))
  }
  const setIntake = (i: 0 | 1) => (it: IntakeState | undefined) =>
    setIntakes((cur) => (i === 0 ? [it, cur[1]] : [cur[0], it]))

  const personIdx = step === 1 || step === 2 ? ((step - 1) as 0 | 1) : null
  const liveIssues: RowIssue[] = showIssues && personIdx !== null ? personRowIssues(inputs.people[personIdx], personIdx) : []

  const removeIssueRows = (rows: RowIssue[]) => {
    if (personIdx === null) return
    setPerson(personIdx)(removeRows(inputs.people[personIdx], rows))
  }
  const fixRow = (field: string) => {
    const el = document.getElementById(field)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el?.focus({ preventScroll: true })
  }
  /** same as the panel's "המשך בכל זאת" — from the error at the bottom */
  const acceptUnadjusted = () => {
    if (personIdx === null) return
    const it = intakes[personIdx]
    if (!it) return
    const built = buildIntake(it.merged, inputs.breakDate, true)
    setPerson(personIdx)({ ...inputs.people[personIdx], ...built.person })
    setIntake(personIdx)(built.intake)
  }
  const showUnadjustedPanel = () => {
    if (personIdx === null) return
    const el = document.getElementById(`p${personIdx}-unadjusted`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el?.focus({ preventScroll: true })
  }

  const next = () => {
    const v = validateStep(step, inputs, intakes)
    if (v.message) { setError(v); setShowIssues(!!v.issues); return }
    setError(null)
    setShowIssues(false)
    setStep((s) => Math.min(s + 1, STEP_LABELS.length - 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  const back = () => {
    setError(null)
    setShowIssues(false)
    setStep((s) => Math.max(0, s - 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <nav aria-label="שלבי האשף" className="mb-4">
        <ol className="flex flex-wrap gap-2 text-xs sm:text-sm">
          {STEP_LABELS.map((label, k) => (
            <li key={label}
              aria-current={k === step ? 'step' : undefined}
              className={`rounded-full px-3 py-1 ${k === step ? 'bg-emerald-800 text-white' : k < step ? 'bg-emerald-100 text-emerald-950' : 'bg-gray-100 text-gray-700'}`}>
              <span className="sr-only">{k < step ? 'הושלם: ' : ''}</span>{k + 1}. {label}
            </li>
          ))}
        </ol>
      </nav>

      <p role="status" aria-live="polite" className="sr-only">שלב {step + 1} מתוך {STEP_LABELS.length}: {STEP_LABELS[step]}</p>

      <h1 ref={headingRef} tabIndex={-1} className="mb-1 font-heading text-2xl font-bold text-gray-900 focus:outline-none">
        {STEP_LABELS[step]}
      </h1>
      <p className="mb-5 text-sm text-gray-600">
        שלב {step + 1} מתוך {STEP_LABELS.length}
        <span className="ms-3" aria-live="polite">
          {saveState === 'saving' ? 'שומר…' : saveState === 'saved' ? 'נשמר' : saveState === 'error' ? 'השמירה נכשלה — ננסה שוב' : ''}
        </span>
      </p>

      {step === 0 && (
        <div className="space-y-6">
          <section className="card">
            <ChoiceButtons
              legend="למי מבני הזוג יש זכויות פנסיוניות?"
              idPrefix="rights"
              value={inputs.rightsHolder}
              invalidId={error?.field}
              errorId={ERROR_ID}
              onChange={(v) => update({ rightsHolder: v })}
              options={[
                { value: 'both', label: 'לשני הצדדים', id: 'rights-both' },
                { value: 'a', label: 'לצד א׳ בלבד', id: 'rights-a' },
                { value: 'b', label: 'לצד ב׳ בלבד', id: 'rights-b' },
              ]}
            />
          </section>
          <section className="card" aria-labelledby="mz-dates">
            <h2 id="mz-dates" className="mb-3 text-base font-semibold text-gray-900">תאריכים</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField id="marriageDate" label="מועד תחילת השותפות (נישואין)" type="date" value={inputs.marriageDate}
                invalidId={error?.field} errorId={ERROR_ID} onChange={(v) => update({ marriageDate: v })} />
              <TextField id="breakDate" label="מועד הקרע" type="date" value={inputs.breakDate}
                hint="מועד הפסקת השיתוף — מועד החישוב. דוח המסלקה מותאם אליו."
                invalidId={error?.field} errorId={ERROR_ID} onChange={(v) => update({ breakDate: v })} />
            </div>
          </section>
        </div>
      )}

      {(step === 1 || step === 2) && (
        <PersonStep
          key={step}
          index={(step - 1) as 0 | 1}
          inputs={inputs}
          intake={intakes[step - 1]}
          invalidId={error?.field}
          errorId={ERROR_ID}
          issues={liveIssues}
          onPerson={setPerson((step - 1) as 0 | 1)}
          onIntake={setIntake((step - 1) as 0 | 1)}
        />
      )}

      {step === 3 && (
        <div className="space-y-6">
          <section className="card" aria-labelledby="mz-case">
            <p className="mb-3 text-sm text-gray-600">אם עדיין אין הליך או מספר תיק — אפשר להשאיר ריק. בפסיקתות יופיע "להשלים", ויש להשלים לפני ההגשה לבית המשפט.</p>
            <h2 id="mz-case" className="mb-3 text-base font-semibold text-gray-900">פרטי ההליך</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField id="court" label="שם הערכאה (אם ידוע)" className="sm:col-span-2" value={inputs.court || ''} placeholder="למשל: בית המשפט לענייני משפחה בתל אביב"
                invalidId={error?.field} errorId={ERROR_ID} onChange={(v) => update({ court: v })} />
              <TextField id="caseNo" label="מספר תיק (אם ידוע)" value={inputs.caseNo || ''} placeholder='תמ"ש / תה"ס'
                invalidId={error?.field} errorId={ERROR_ID} onChange={(v) => update({ caseNo: v })} />
              <TextField id="judge" label="שם המותב (לא חובה)" value={inputs.judge || ''} onChange={(v) => update({ judge: v })} />
            </div>
          </section>
          <details className="card">
            <summary className="cursor-pointer text-base font-semibold text-gray-900">הנחות התחשיב (מתקדם)</summary>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField id="ratePct" label="שיעור היוון שנתי (%)" type="number" min={0} step="0.05" value={inputs.ratePct}
                hint="ברירת מחדל: 3%" onChange={(v) => update({ ratePct: Math.min(15, Math.max(0, parseFloat(v) || 0)) })} />
              <TextField id="taxPct" label="שיעור מס על יתרות (%)" type="number" min={0} step={1} value={inputs.taxPct}
                hint="ברירת מחדל: 35%" onChange={(v) => update({ taxPct: Math.min(60, Math.max(0, parseFloat(v) || 0)) })} />
            </div>
          </details>
        </div>
      )}

      {step === 4 && <SummaryStep reportId={id} inputs={inputs} flush={flush} />}

      {liveIssues.length > 0 ? (
        <div id={ERROR_ID} ref={errorRef} tabIndex={-1} role="alert" className="mt-5 rounded-xl border-2 border-red-400 bg-red-50 p-4 text-sm text-red-950 focus:outline-none">
          <p className="flex items-start gap-2 text-base font-semibold">
            <AlertTriangle size={20} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            {liveIssues.length === 1 ? 'שורה אחת חסרה נתונים' : `${liveIssues.length} שורות חסרות נתונים`} — לכן אי אפשר להמשיך לשלב הבא
          </p>
          <p className="mt-1">השלימו את הנתונים החסרים, או הסירו שורה שאינה רלוונטית (למשל קופה שאין בה קצבה צפויה).</p>
          <ul className="mt-3 space-y-2">
            {liveIssues.map((x) => (
              <li key={`${x.section}-${x.index}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-white p-2 ring-1 ring-red-200">
                <span className="flex-1">
                  <strong>{SECTION_LABEL[x.section]}: {x.label}</strong> — חסר: {x.missing.join(', ')}
                </span>
                <button type="button" className="font-semibold text-primary-700 underline" onClick={() => fixRow(x.field)}>
                  השלמה<span className="sr-only"> של {x.label}</span>
                </button>
                <button type="button" className="inline-flex items-center gap-1 text-red-800 underline" onClick={() => removeIssueRows([x])}>
                  <Trash2 size={14} aria-hidden="true" /> הסרה<span className="sr-only"> של {x.label}</span>
                </button>
              </li>
            ))}
          </ul>
          {liveIssues.length > 1 && (
            <button type="button" className="btn btn-danger mt-3 gap-2" onClick={() => removeIssueRows(liveIssues)}>
              <Trash2 size={16} aria-hidden="true" /> הסרת כל {liveIssues.length} השורות החסרות
            </button>
          )}
        </div>
      ) : error?.unadjusted ? (
        <div id={ERROR_ID} ref={errorRef} tabIndex={-1} role="alert" className="mt-5 rounded-xl border-2 border-amber-500 bg-amber-50 p-4 text-sm text-amber-950 focus:outline-none">
          <p className="flex items-start gap-2 text-base font-semibold">
            <AlertTriangle size={20} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            דוח המסלקה אינו מתאים למועד הקרע: {error.message}
          </p>
          <p className="mt-1">אפשר להמשיך לפי הדוח הנוכחי (ללא התאמה למועד הקרע — יצוין בחוות הדעת), או להעלות דוח מתאים.</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button type="button" className="btn btn-primary gap-2 py-3 text-base shadow-md" onClick={acceptUnadjusted}>
              המשך בכל זאת לפי הדוח הנוכחי <ArrowLeft size={18} aria-hidden="true" />
            </button>
            <button type="button" className="font-medium underline" onClick={showUnadjustedPanel}>הצגת הפרטים והעלאת דוח אחר</button>
          </div>
        </div>
      ) : error && !error.issues ? (
        <p id={ERROR_ID} role="alert" className="mt-5 rounded-lg bg-red-50 p-3 text-sm font-medium text-red-800">
          {error.message}
        </p>
      ) : null}

      <div className="mt-6 flex items-center justify-between gap-3">
        <button type="button" className="btn btn-secondary inline-flex items-center gap-1" onClick={back} disabled={step === 0}>
          <ChevronRight size={18} aria-hidden="true" /> הקודם
        </button>
        {step < STEP_LABELS.length - 1 && (
          <button type="button" className="btn btn-primary inline-flex items-center gap-1" onClick={next}>
            הבא <ChevronLeft size={18} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  )
}
